// Illustrative resting physiology for a healthy 31-year-old adult, not patient data.
// Morphology landmarks: https://pmc.ncbi.nlm.nih.gov/articles/PMC10942507/
export const RESTING_HEART_RATE = 72;
export const RESPIRATORY_RATE = 14;
export const RESTING_TEMPERATURE_C = 36.8;
export const SWEEP_SPEED = 100; // CSS pixels / second, independent of refresh rate.

const TWO_PI = 2 * Math.PI;
const RESPIRATORY_FREQUENCY = RESPIRATORY_RATE / 60;

export const respiratoryPhase = (seconds) => TWO_PI * RESPIRATORY_FREQUENCY * seconds;

// Relative thoracic expansion; one smooth inspiration/expiration per breath.
// The rising half-cycle coincides with the inspiratory increase in heart rate.
export const respirationAmplitude = (seconds) => (1 - Math.cos(respiratoryPhase(seconds))) / 2;

export const cardiacCycles = (seconds) =>
  (RESTING_HEART_RATE / 60) * seconds +
  (3 / (60 * TWO_PI * RESPIRATORY_FREQUENCY)) * (1 - Math.cos(respiratoryPhase(seconds)));

export const cardiacPhase = (seconds) => {
  const cycles = cardiacCycles(seconds);
  return cycles - Math.floor(cycles);
};

export const heartRate = (seconds) => RESTING_HEART_RATE + 3 * Math.sin(respiratoryPhase(seconds));

// Invert the shared cardiac clock: every integer cycle is an ECG R peak.
// Absolute beat times let ECG intervals stay fixed in seconds as the RR varies.
export const cardiacBeatTime = (cycle) => {
  let time = (cycle * 60) / RESTING_HEART_RATE;
  for (let i = 0; i < 5; i += 1) {
    time -= (cardiacCycles(time) - cycle) / (heartRate(time) / 60);
  }
  return time;
};

// Monotone cubic Hermite interpolation preserves each landmark without overshoot.
// Zero slopes at extrema give a rounded peak and notch, with a continuous foot.
export const createContour = (points) => {
  const slopes = points.map(([x, y], index) => {
    if (index === 0 || index === points.length - 1) return 0;
    const [previousX, previousY] = points[index - 1];
    const [nextX, nextY] = points[index + 1];
    const leftWidth = x - previousX;
    const rightWidth = nextX - x;
    const leftSlope = (y - previousY) / leftWidth;
    const rightSlope = (nextY - y) / rightWidth;
    if (leftSlope * rightSlope <= 0) return 0;
    const firstWeight = 2 * rightWidth + leftWidth;
    const secondWeight = rightWidth + 2 * leftWidth;
    return (firstWeight + secondWeight) / (firstWeight / leftSlope + secondWeight / rightSlope);
  });

  return (phase) => {
    if (phase < points[0][0] || phase >= points[points.length - 1][0]) return 0;
    const index = points.findIndex((point, i) => i < points.length - 1 && phase < points[i + 1][0]);
    if (index < 0) return 0;
    const [x, y] = points[index];
    const [nextX, nextY] = points[index + 1];
    const width = nextX - x;
    const t = (phase - x) / width;
    return (
      (2 * t ** 3 - 3 * t ** 2 + 1) * y +
      (t ** 3 - 2 * t ** 2 + t) * width * slopes[index] +
      (-2 * t ** 3 + 3 * t ** 2) * nextY +
      (t ** 3 - t ** 2) * width * slopes[index + 1]
    );
  };
};

// Peripheral arterial pressure: brisk ejection, incisura, small rebound, runoff.
export const arterialContour = createContour([
  [0, 0],
  [0.035, 0.13],
  [0.085, 0.77],
  [0.13, 1],
  [0.19, 0.86],
  [0.29, 0.55],
  [0.36, 0.36],
  [0.385, 0.3],
  [0.415, 0.36],
  [0.47, 0.31],
  [0.6, 0.18],
  [0.78, 0.07],
  [1, 0],
]);

// Finger PPG: broader systolic peak and a softer, later reflected diastolic wave.
export const plethContour = createContour([
  [0, 0],
  [0.055, 0.12],
  [0.12, 0.67],
  [0.19, 1],
  [0.26, 0.87],
  [0.36, 0.51],
  [0.405, 0.38],
  [0.48, 0.46],
  [0.58, 0.33],
  [0.74, 0.12],
  [0.9, 0.02],
  [1, 0],
]);

export const pressureProfile = (seconds) => {
  const respiration = Math.sin(respiratoryPhase(seconds));
  return { systolic: 118 + 1.2 * respiration, diastolic: 72 + 0.7 * respiration };
};

export const arterialPressure = (seconds) => {
  const { systolic, diastolic } = pressureProfile(seconds);
  // Pulse arrival follows electrical ventricular activation.
  return diastolic + (systolic - diastolic) * arterialContour(cardiacPhase(seconds - 0.16));
};

export const plethAmplitude = (seconds) => {
  const respiration = Math.sin(respiratoryPhase(seconds));
  return (
    (1 + 0.025 * respiration) * plethContour(cardiacPhase(seconds - 0.24)) + 0.008 * respiration
  );
};

// All pressure readouts come from the same last complete beat: extrema for
// systolic/diastolic pressure and the time average for MAP.
export const arterialVitals = (seconds) => {
  const beat = Math.floor(cardiacCycles(seconds - 0.16));
  const start = cardiacBeatTime(beat - 1) + 0.16;
  const duration = cardiacBeatTime(beat) + 0.16 - start;
  const sampleCount = 256;
  let total = 0;
  let systolic = -Infinity;
  let diastolic = Infinity;
  for (let i = 0; i < sampleCount; i += 1) {
    const pressure = arterialPressure(start + ((i + 0.5) / sampleCount) * duration);
    total += pressure;
    systolic = Math.max(systolic, pressure);
    diastolic = Math.min(diastolic, pressure);
  }
  return { systolic, diastolic, map: total / sampleCount };
};

export const meanArterialPressure = (seconds) => arterialVitals(seconds).map;
